import React from 'react';
import CustomToolTip from '../CustomToolTip';
import { LockStatusBadgeIcon } from '../../Missions/Satellites/TestCase/svgs/TestCaseSvgs';
import { useItemLock, formatLockTimestamp } from '../../../hooks/useItemLock';
import { LockMetadata } from '../../../types/validationLockTypes';

import './LockStyles.scss';

interface LockStatusBadgeProps {
  item?: LockMetadata | null;
  locked?: boolean;
  lastLockUpdateBy?: string;
  lockedBy?: string;
  lastLockUpdateAt?: string | number;
  lockedAt?: string;
  referencingPlans?: string[];
  tooltipTitle?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

/**
 * Drop-in status badge rendering a right-aligned Blue Lock SVG with tooltip.
 * If the item is not locked, renders nothing (zero layout impact).
 */
export const LockStatusBadge: React.FC<LockStatusBadgeProps> = ({
  item,
  locked,
  lastLockUpdateBy,
  lockedBy,
  lastLockUpdateAt,
  lockedAt,
  referencingPlans,
  tooltipTitle,
  placement = 'top',
  className = '',
}) => {
  const lockState = useItemLock(item);
  const isLocked = locked !== undefined ? locked : lockState.isLocked;

  if (!isLocked) {
    return null;
  }

  const effectiveBy = lastLockUpdateBy || lockedBy || item?.lastLockUpdateBy || item?.lockedBy;
  const effectiveAt = lastLockUpdateAt || lockedAt || item?.lastLockUpdateAt || item?.lockedAt;

  let computedTitle = tooltipTitle;
  if (!computedTitle) {
    if (referencingPlans && referencingPlans.length > 0) {
      const planNames = referencingPlans.map(p => `'${p}'`).join(', ');
      computedTitle = referencingPlans.length > 1
        ? `Locked by Test Plans ${planNames}. Unlock the plans first.`
        : `Locked by Test Plan ${planNames}. Unlock the plan first.`;
    } else if (effectiveBy && effectiveAt) {
      const dateStr = formatLockTimestamp(effectiveAt);
      computedTitle = dateStr ? `Locked by ${effectiveBy} on ${dateStr}` : `Locked by ${effectiveBy}`;
    } else if (effectiveBy) {
      computedTitle = `Locked by ${effectiveBy}`;
    } else if (effectiveAt) {
      const dateStr = formatLockTimestamp(effectiveAt);
      computedTitle = dateStr ? `Locked on ${dateStr}` : (lockState.lockTooltip || 'Item is locked');
    } else {
      computedTitle = lockState.lockTooltip || 'Item is locked';
    }
  }

  return (
    <CustomToolTip
      title={computedTitle}
      placement={placement}
      className={`validation-lock-badge-wrapper ${className}`.trim()}
    >
      <span
        className="validation-lock-badge"
        onClick={(e) => e.stopPropagation()}
      >
        <LockStatusBadgeIcon />
      </span>
    </CustomToolTip>
  );
};

export default LockStatusBadge;
